//@flow
import React, { useState, useEffect } from "react";
import { compose } from "redux";
import { connect } from "react-redux";
import { localize } from "redux-i18n";
import { push } from "connected-react-router";
import { route } from "@routes/helper";

import { GSGrid, View, ButtonGroup, Button, Loader } from "@components";
import { AlertsAddModal } from "@modules/service/components";
import { formatDate } from "@transformers/date";

import actionCreators from "@modules/service/actionCreator";
import sharedActionCreators from "@modules/shared/actionCreator";
import * as serviceSelectors from "@modules/service/selectors";

import type { State } from "@store/reducers";
import type { ApprovableContainer } from "@modules/service/types";
import type { Alert } from "@modules/service/reducer/alerts/types";

import { selectActiveHierarchyStructure } from "@modules/data/selectors";

export type FilterState = null | true | false;

type OwnProps = $Exact<{
  clientId: number,
}>;
type PropsFromDispatch = $Exact<{
  getList: typeof actionCreators.GetAlertsList,
  newAlert: typeof actionCreators.NewAlert,
  getHierarchyTree: typeof sharedActionCreators.GetHierarchyTree,
  getAbsenceReasons: typeof sharedActionCreators.GetAbsenceReasons,
  getSpecialContactTypes: typeof sharedActionCreators.GetSpecialContactTypes,
  getStaffGroups: typeof actionCreators.GetStaffGroups,
  getSchema: typeof actionCreators.GetAlertSchema,
  push: typeof push,
}>;

type PropsFromState = $Exact<{
  list: Array<ApprovableContainer<Alert>>,
  isLoading: boolean,
  hierarchyTree: Object | null,
  container: ?ApprovableContainer<Alert>,
}>;

type Props = $Exact<{
  clientId: number,
  ...OwnProps,
  ...PropsFromDispatch,
  ...PropsFromState,
  ...PropsFromLocalize,
}>;

function AlertTriggers(props: Props) {
  const [filter, setFilter] = useState<FilterState>(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const { clientId } = props;

  useEffect(() => {
    props.getHierarchyTree({ clientId });
    props.getAbsenceReasons({ clientId });
    props.getSpecialContactTypes({ clientId });
    props.getStaffGroups({ clientId, isActive: filter });
    props.getSchema({ clientId });
  }, [clientId]);

  useEffect(() => {
    props.getList({
      clientId,
      isActive: filter,
    });
  }, [filter, clientId]);

  const data = props?.list || [];

  const toggleModal = () => {
    setIsModalOpen(prev => !prev);
  };

  const columns = [
    {
      name: props.t("ID"),
      align: "center",
      key: "id",
    },
    {
      name: props.t("ALERTS_ALERT_NAME"),
      width: "min-content",
      renderCell: ({ row }: { row: ApprovableContainer<Alert> }) =>
        GSGrid.render.ellipsis(row.content.details.name),
      align: "left",
    },
    {
      name: props.t("ALERTS_ALERT_PURPOSE"),
      renderCell: ({ row }: { row: ApprovableContainer<Alert> }) =>
        props.t(row.content.details.alertPurpose),
      align: "left",
    },

    {
      key: "attachments",
      name: props.t("Attachments"),
      tooltip: {
        title: props.t("BEST_PRACTICE"),
        text: props.t("SERVICE_FEATURES_ALERTS_TABLE_ATTACHMENTS"),
      },
      renderCell: ({ row }: { row: ApprovableContainer<Alert> }) => {
        const attachments = getValuesAndScore(row).attachments;
        return getColorPillRender(attachments.value, attachments.isScoring);
      },
    },
    {
      key: "recipients",
      name: props.t("RECIPIENTS"),
      tooltip: {
        title: props.t("BEST_PRACTICE"),
        text: props.t("SERVICE_FEATURES_ALERTS_TABLE_RECIPIENTS"),
      },
      renderCell: ({ row }: { row: ApprovableContainer<Alert> }) => {
        const bespokeRecipients = getValuesAndScore(row).bespokeRecipients;
        return getColorPillRender(bespokeRecipients.value, bespokeRecipients.isScoring);
      },
    },
    {
      key: "hierarchyAreas",
      name: props.t("HIERARCHY_AREAS"),
      tooltip: {
        title: props.t("BEST_PRACTICE"),
        text: props.t("SERVICE_FEATURES_ALERTS_TABLE_HIERARCHY_AREAS"),
      },
      renderCell: ({ row }: { row: Object }) => {
        const hierarchyAssignments = getValuesAndScore(row).hierarchyAssignments;
        return getColorPillRender(hierarchyAssignments.value, hierarchyAssignments.isScoring);
      },
    },
    {
      key: "fields",
      name: props.t("FIELDS"),
      tooltip: {
        title: props.t("BEST_PRACTICE"),
        text: props.t("SERVICE_FEATURES_ALERTS_TABLE_HIERARCHY_FIELDS"),
      },
      renderCell: ({ row }: { row: ApprovableContainer<Alert> }) => {
        const emailContent = getValuesAndScore(row).emailContent;
        return getColorPillRender(emailContent.value, emailContent.isScoring);
      },
    },
    {
      key: "triggerPoint",
      name: props.t("TRIGGER_POINT"),
      tooltip: {
        title: props.t("BEST_PRACTICE"),
        text: props.t("SERVICE_FEATURES_ALERTS_TABLE_HIERARCHY_TRIGGER_POINT"),
      },
      renderCell: ({ row }: { row: ApprovableContainer<Alert> }) => {
        const triggerMode = getValuesAndScore(row).triggerMode;
        return getColorPillRender(triggerMode.value, triggerMode.isScoring);
      },
    },
    {
      key: "rating",
      name: props.t("RATING"),
      renderCell: ({ row }: { row: ApprovableContainer<Alert> }) => {
        const valuesAndScore = Object.values(getValuesAndScore(row));

        if (!Array.isArray(valuesAndScore)) return;
        //$FlowIgnore
        const totalScore = valuesAndScore.filter(v => v.isScoring === true).length;
        return GSGrid.render.scorePill(totalScore);
      },
    },

    {
      name: props.t("STATUS"),
      renderCell: ({ row }: Object) =>
        GSGrid.render.statusPill(
          row.content.details.isActive ? props.t("ACTIVE") : props.t("INACTIVE"),
        ),
    },
    {
      name: props.t("VIEW"),
      key: "action_menu",
      width: "min-content",
      frozen: "right",
      onCellClick: (row: Object) => handleViewRevision(row.id),
      renderCell: ({ row }: Object) => GSGrid.render.materialIcon("search"),
    },
  ];

  type ValueAndScore = {
    value: any,
    isScoring: boolean,
  };

  type ValuesAndScoreRecord = {
    attachments: ValueAndScore,
    bespokeRecipients: ValueAndScore,
    hierarchyAssignments: ValueAndScore,
    emailContent: ValueAndScore,
    triggerMode: ValueAndScore,
  };

  function getValuesAndScore(row: ApprovableContainer<Alert>): ValuesAndScoreRecord {
    const notifyEmployeeContacts = !!row.content.details.notifyEmployeeContacts ? 1 : 0;
    const attachments = row.content.attachments.length;
    const bespokeRecipients = row.content.bespokeRecipients.length + notifyEmployeeContacts;
    const hierarchyAssignments = row.hierarchyAssignments?.length || 0;
    const emailContent = Object.values(row.content.emailContent).filter(Boolean).length;
    const triggerMode = ["PREDICTED", "ACTUAL_TIME"].includes(row.content.details.triggerMode);
    return {
      attachments: { value: attachments, isScoring: attachments > 0 },
      bespokeRecipients: { value: bespokeRecipients, isScoring: bespokeRecipients > 0 },
      hierarchyAssignments: { value: hierarchyAssignments, isScoring: hierarchyAssignments > 0 },
      emailContent: { value: emailContent, isScoring: emailContent >= 3 },
      triggerMode: { value: props.t(row.content.details.triggerMode), isScoring: triggerMode },
    };
  }

  function getColorPillRender(value: number | string | boolean, isGreen: boolean) {
    return GSGrid.render.colorPill(value, isGreen && "mint", isGreen && "mint-d2");
  }

  function handleViewRevision(id: number) {
    props.push(
      route("ADMINISTRATION_SERVICE_EVENT_DETAILS", {
        clientId,
        alertId: id,
        isActive: filter,
      }),
    );
  }
  function handleAddSetup() {
    props.newAlert();
    toggleModal();
  }
  function handleChangeFilter(filter: FilterState) {
    return function() {
      setFilter(filter);
    };
  }

  return (
    <View>
      {props.isLoading && <Loader />}
      <View className="message-box" margin={[0, 0, "2rem", 0]}>
        {props.t("ALERTS_INFORMATION")}
      </View>
      <View direction="row" justifyContent="space-between" margin={[0, 0, "1rem", 0]}>
        <ButtonGroup
          config={[
            {
              color: filter === null && "selected",
              label: props.t("ALL"),
              onClick: handleChangeFilter(null),
            },
            {
              color: filter === true && "selected",
              label: props.t("ACTIVE"),
              onClick: handleChangeFilter(true),
            },
            {
              color: filter === false && "selected",
              label: props.t("INACTIVE"),
              onClick: handleChangeFilter(false),
            },
          ]}
        ></ButtonGroup>
        <Button materialIcon="add_box" onClick={handleAddSetup}>
          {props.t("ADD")}
        </Button>
      </View>

      {data.length ? (
        <GSGrid columns={columns} rows={data} />
      ) : (
        <h3 className="text-pinkish-grey">{props.t("ALERTS_LIVE_REASONS")}</h3>
      )}
      {isModalOpen && (
        <AlertsAddModal
          clientId={props.clientId}
          isActive={filter}
          container={props.container}
          onCancel={toggleModal}
        />
      )}
    </View>
  );
}

const mapStateToProps = (state: State): PropsFromState => {
  return {
    list: serviceSelectors.getAlertsList(state),
    isLoading: serviceSelectors.isLoading(state),
    hierarchyTree: selectActiveHierarchyStructure(state),
    container: serviceSelectors.getCurrentAlert(state),
  };
};

const mapDispatchToProps: PropsFromDispatch = {
  getList: actionCreators.GetAlertsList,
  newAlert: actionCreators.NewAlert,
  getHierarchyTree: sharedActionCreators.GetHierarchyTree,
  getAbsenceReasons: sharedActionCreators.GetAbsenceReasons,
  getSpecialContactTypes: sharedActionCreators.GetSpecialContactTypes,
  getStaffGroups: actionCreators.GetStaffGroups,
  getSchema: actionCreators.GetAlertSchema,
  push: push,
};

export default compose(localize(), connect(mapStateToProps, mapDispatchToProps))(AlertTriggers);
